import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { LANGUAGES, useI18n } from "@/context/I18nContext";

/**
 * Dynamic SEO head manager:
 * - Sets <html lang="...">
 * - Injects/updates <link rel="alternate" hreflang="...">  for all supported languages
 * - Injects/updates <link rel="canonical">
 * - Updates page <title> and <meta name="description">
 * Hreflang strategy: the same URL is served for every language (SPA),
 * so we differentiate via the ?lang= query param convention which Google
 * understands as a valid language signal.
 */
export default function SeoHead() {
  const { lang } = useI18n();
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof document === "undefined") return;
    const origin = window.location.origin;
    const path = pathname || "/";

    // 1. html[lang]
    document.documentElement.lang = lang;

    // 2. Remove previously-injected hreflang + canonical (data-seo attribute)
    document.querySelectorAll('link[data-seo="hreflang"], link[data-seo="canonical"]').forEach((n) => n.remove());

    const head = document.head;

    // 3. Insert hreflang for every supported language + x-default
    LANGUAGES.forEach(({ code }) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", code);
      link.setAttribute("href", `${origin}${path}?lang=${code}`);
      link.setAttribute("data-seo", "hreflang");
      head.appendChild(link);
    });
    // x-default → English
    const xdef = document.createElement("link");
    xdef.setAttribute("rel", "alternate");
    xdef.setAttribute("hreflang", "x-default");
    xdef.setAttribute("href", `${origin}${path}`);
    xdef.setAttribute("data-seo", "hreflang");
    head.appendChild(xdef);

    // 4. Canonical
    const can = document.createElement("link");
    can.setAttribute("rel", "canonical");
    can.setAttribute("href", `${origin}${path}`);
    can.setAttribute("data-seo", "canonical");
    head.appendChild(can);

    // 5. Open Graph locale + alternate
    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      }
    };
    const ogLocale = { de: "de_DE", en: "en_GB", it: "it_IT", fr: "fr_FR", es: "es_ES", pt: "pt_PT", nl: "nl_NL", pl: "pl_PL", cs: "cs_CZ", sk: "sk_SK", hu: "hu_HU", ro: "ro_RO", bg: "bg_BG", hr: "hr_HR", sl: "sl_SI", el: "el_GR", sv: "sv_SE", da: "da_DK", fi: "fi_FI" }[lang] || "en_GB";
    setMeta('meta[property="og:locale"]', { property: "og:locale", content: ogLocale });

    // Remove old og:locale:alternate then re-add
    document.head.querySelectorAll('meta[property="og:locale:alternate"][data-seo="og-alt"]').forEach((n) => n.remove());
    LANGUAGES.forEach(({ code }) => {
      if (code === lang) return;
      const og = document.createElement("meta");
      og.setAttribute("property", "og:locale:alternate");
      og.setAttribute("content", { de: "de_DE", en: "en_GB", it: "it_IT", fr: "fr_FR", es: "es_ES", pt: "pt_PT", nl: "nl_NL", pl: "pl_PL", cs: "cs_CZ", sk: "sk_SK", hu: "hu_HU", ro: "ro_RO", bg: "bg_BG", hr: "hr_HR", sl: "sl_SI", el: "el_GR", sv: "sv_SE", da: "da_DK", fi: "fi_FI" }[code]);
      og.setAttribute("data-seo", "og-alt");
      document.head.appendChild(og);
    });
  }, [lang, pathname]);

  return null;
}
